import { default as uploadApi } from '@repo/editor/src/api/user';
import { buildImageProxyUrl } from '@repo/utils/url';

import generatorsApi from 'global/generatorsApi';

export enum UploadError {
  UPLOAD_SPACE_LIMIT_REACHED = 'Your image could not be uploaded. Upload space limit reached.',
  UNKNOWN_UPLOAD_ERROR = 'Your image could not be uploaded. Try again.',
}

export enum RemoveBackgroundError {
  UNKNOWN_BACKGROUND_REMOVER_ERROR = 'Background could not be removed. Try again.',
}

type Response<T> = Promise<
  { url: string; error?: undefined } | { url: null; error: T }
>;

export const buildImageProxyUrlByObjectName = (
  objectName: string,
  width?: number,
  height?: number,
  format?: string
): string => {
  return buildImageProxyUrl(`api/${objectName}`, {
    width: width,
    height: height,
    format: format ?? undefined,
    keepFormat: format ? undefined : true,
  });
};

export const uploadImage = async (
  file: File,
  width?: number,
  height?: number,
  format?: string,
  isTemporaryFile = false
): Response<UploadError> => {
  const uploadResult = await uploadApi.uploadElement(
    file,
    false,
    isTemporaryFile
  );
  if ('error' in uploadResult) {
    if (uploadResult.error === 'UPLOAD_SPACE_LIMIT_REACHED') {
      return {
        url: null,
        error: UploadError.UPLOAD_SPACE_LIMIT_REACHED,
      };
    }
    return { url: null, error: UploadError.UNKNOWN_UPLOAD_ERROR };
  }

  if (!uploadResult.objectName) {
    return { url: null, error: UploadError.UNKNOWN_UPLOAD_ERROR };
  }

  const uploadUrl = buildImageProxyUrlByObjectName(
    uploadResult.objectName,
    width,
    height,
    format
  );
  return { url: uploadUrl };
};

export const tryDeleteImage = async (
  id: string
): Promise<{ error: string | null }> => {
  const result = await uploadApi.deleteUpload(id);
  if (result.error) {
    return { error: result.error };
  }
  return { error: null };
};

export const removeBackground = async (
  url: string,
  width: number,
  height: number
): Response<RemoveBackgroundError> => {
  const backgroundResult = await generatorsApi.removeBackground(url);

  if (!backgroundResult.success) {
    return {
      url: null,
      error: RemoveBackgroundError.UNKNOWN_BACKGROUND_REMOVER_ERROR,
    };
  }

  return {
    url: buildImageProxyUrlByObjectName(
      backgroundResult.objectName,
      width,
      height
    ),
  };
};

export const downloadFile = async (
  url: string,
  filename: string
): Promise<void> => {
  const result = await fetch(url);
  const blob = await result.blob();
  const href = URL.createObjectURL(blob);
  const element = document.createElement('a');
  element.setAttribute('href', href);
  element.setAttribute('download', filename);
  element.setAttribute('target', '_blank');
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  URL.revokeObjectURL(href);
  document.body.removeChild(element);
};
